import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfessionalData } from '../hooks/useProfessionalData';
import RescheduleModal from './RescheduleModal';
import DashboardProfileHeader from './DashboardProfileHeader';
import SubscriptionBanner from './SubscriptionBanner';
import VencimientoSuscripcion from './VencimientoSuscripcion';
import SubscriptionExpirySoonBanner from './SubscriptionExpirySoonBanner';
import WhatsAppQuotaIndicator from './WhatsAppQuotaIndicator';
import LoadingSpinner from './LoadingSpinner';
import SubscriptionModal from './SubscriptionModal';
import SubscriptionExpiredModal from './SubscriptionExpiredModal';
import { isSubscriptionExpiredError } from '../utils/subscriptionUtils';
import { getExtraLabel, formatExtraValue } from '../utils/patientExtraData';

import ShareProfileButton from './ShareProfileButton';
import RemindersQuickAccess from './RemindersQuickAccess';
import { useSubscription } from '../hooks/useSubscription';
import OnboardingChecker from './OnboardingChecker';

// Importar iconos de Lucide
import {
  Calendar,
  Users,
  Image,
  BarChart3,
  Settings,
  Clock,
  DollarSign,
  MessageCircle,
  CreditCard,
  Plus,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileSpreadsheet,
  Sparkles,
  MapPin,           // ✅ NUEVO: Icono para Sedes
  ListChecks,       // ✅ NUEVO: Icono para Campos de Booking
  HelpCircle
} from 'lucide-react';
import ExportDateRangeModal from './ExportDateRangeModal';
import RemindersPanel from './RemindersPanel';
import NewRemindersGuideModal from './NewRemindersGuideModal';
import AppointmentNoteEditor from './AppointmentNoteEditor';

// =====================================================
// INTERFACES LOCALES
// =====================================================

interface BeautyUser {
  id: number;
  email: string;
  nombre: string;
  apellido: string;
  es_profesional: boolean;
  professional_type: 'belleza';
  especialidad: string;
  auth_method: string;
  picture?: string;
  cover_photo?: string;
}

// =====================================================
// FUNCIONES DE UTILIDAD MEJORADAS
// =====================================================

const getToday = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatLocalDate = (dateString: string) => {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const BeautyProfessionalDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<BeautyUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedAppointmentForReschedule, setSelectedAppointmentForReschedule] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showRemindersPanel, setShowRemindersPanel] = useState(false);
  const [showRemindersGuide, setShowRemindersGuide] = useState(false);

  // La guía de recordatorios ya NO se abre sola (a usuarios con localStorage
  // no persistente les aparecía en cada ingreso). Queda accesible solo desde
  // los botones "ver guía" del dashboard.

  const { isExpired, daysRemaining } = useSubscription();

  const {
    metrics,
    professional,
    appointments,
    services,
    loadingMetrics,
    loadingAppointments,
    loadingServices,
    metricsError,
    appointmentsError,
    servicesError,
    actionLoading,
    actionError,
    updateAppointmentStatus,
    refreshData,
    clearErrors,
    clearActionState,
    getAvailableActions,
    isDateInPast,
    isDateToday,
    rescheduleAppointment
  } = useProfessionalData({
    professionalId: user?.id,
    autoLoad: false,
    selectedDate
  });

  // =====================================================
  // EFECTOS OPTIMIZADOS
  // =====================================================

  useEffect(() => {
    const initDashboard = async () => {
      const userData = localStorage.getItem('user');
      const loginStatus = localStorage.getItem('isLoggedIn');
      
      if (!userData || loginStatus !== 'true') {
        navigate('/login');
        return;
      }

      try {
        const parsedUser = JSON.parse(userData);
        
        if (!parsedUser.es_profesional || parsedUser.professional_type !== 'belleza') {
          navigate('/dashboard/profesional');
          return;
        }
        
        // CONSULTAR EL BACKEND PRIMERO para obtener status actualizado
        try {
          const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
          const token = localStorage.getItem('token');
          
          if (token) {
            const response = await fetch(`${API_URL}/api/auth/me`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (response.ok) {
              const data = await response.json();
              const freshUser = data.user;
              
              // Actualizar localStorage con datos frescos
              localStorage.setItem('user', JSON.stringify(freshUser));
              localStorage.setItem('subscription_status', freshUser.subscription_status);
              
              console.log('✅ Usuario actualizado desde backend:', {
                subscription_status: freshUser.subscription_status
              });
              
              // Ahora verificar con datos frescos: tanto trial como pago vencidos
              const expiredByState = freshUser.subscription && freshUser.subscription.isExpired;
              const expiredByStatus =
                freshUser.subscription_status === 'trial_expired' ||
                freshUser.subscription_status === 'expired';
              if (expiredByState || expiredByStatus) {
                console.log('🚫 Suscripción expirada detectada:', {
                  effectiveStatus: freshUser.subscription && freshUser.subscription.effectiveStatus,
                  subscription_status: freshUser.subscription_status,
                });
                setUser(freshUser);
                setShowExpiredModal(true);
                setLoading(false);
                return;
              }
              
              setUser(freshUser);
              console.log('💼 Usuario de belleza verificado:', freshUser);
              setLoading(false);
              return;
            }
          }
        } catch (error) {
          console.error('Error consultando backend:', error);
          // Continuar con localStorage si falla
        }
        
        // Si falló la consulta, usar localStorage (fallback)
        const subscriptionStatus = localStorage.getItem('subscription_status');
        const trialEndsAt = localStorage.getItem('trial_ends_at');
        
        // Detectar si el backend ya marcó como expirado (trial o pago)
        if (subscriptionStatus === 'trial_expired' || subscriptionStatus === 'expired') {
          console.log('🚫 Suscripción expirada detectada por backend:', subscriptionStatus);
          setUser(parsedUser);
          setShowExpiredModal(true);
          setLoading(false);
          return;
        }
        
        // O verificar manualmente si está en trial y la fecha pasó
        if (subscriptionStatus === 'trial' && trialEndsAt) {
          const trialEndDate = new Date(trialEndsAt);
          const now = new Date();
          
          if (now > trialEndDate) {
            console.log('🚫 Trial expirado detectado por fecha');
            setUser(parsedUser);
            setShowExpiredModal(true);
            setLoading(false);
            return;
          }
        }
        
        setUser(parsedUser);
        console.log('💼 Usuario de belleza verificado:', parsedUser);
        
      } catch (error) {
        console.error('Error parsing user data:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    
    initDashboard();
  }, [navigate]);

  useEffect(() => {
    // NO CARGAR DATOS SI EL MODAL DE EXPIRACIÓN ESTÁ ACTIVO
    if (showExpiredModal) {
      console.log('⏸️ Carga de datos pausada - Trial expirado');
      return;
    }
    
    if (user?.id && !loading) {
      console.log('🔄 Cargando datos REALES de belleza para profesional:', user.id);
      refreshData();
    }
  }, [user?.id, loading, showExpiredModal]);

  useEffect(() => {
    if (isExpired && !showSubscriptionModal) {
      setShowSubscriptionModal(true);
    }
  }, [isExpired, showSubscriptionModal]);

  // =====================================================
  // FUNCIONES DE MANEJO DE CITAS
  // =====================================================

  const handleAppointmentAction = async (appointmentId: number, action: string) => {
    if (!user?.id) return;
    
    console.log(`🎯 Acción ${action} en cita ${appointmentId}`);
    clearActionState();
    
    try {
      let success = false;
      
      switch (action) {
        case 'confirmar':
          success = await updateAppointmentStatus(user.id, appointmentId, 'confirmed');
          break;
        case 'completar':
          success = await updateAppointmentStatus(user.id, appointmentId, 'completed');
          break;
        case 'cancelar':
          success = await updateAppointmentStatus(user.id, appointmentId, 'cancelled', undefined, 'Cancelación solicitada por el profesional');
          break;
        case 'no_show':
          success = await updateAppointmentStatus(user.id, appointmentId, 'no_show');
          break;
        default:
          console.warn('Acción no reconocida:', action);
          return;
      }
      
      if (success) {
        alert(`✅ Cita ${action} exitosamente`);
      } else {
        alert(`❌ Error al ${action} la cita`);
      }
    } catch (error) {
      console.error(`Error en acción ${action}:`, error);
      alert(`❌ Error al ${action} la cita`);
    }
  };

  const handleReschedule = (appointment: any) => {
    setSelectedAppointmentForReschedule(appointment);
    setShowRescheduleModal(true);
  };

  const handleRescheduleSuccess = () => {
    setShowRescheduleModal(false);
    setSelectedAppointmentForReschedule(null);
    if (user?.id) {
      refreshData();
    }
  };

  const renderAppointmentActions = (appointment: any) => {
    const availableActions = getAvailableActions(appointment);
    const isPastDate = isDateInPast(appointment.appointment_date);
    const isTodayDate = isDateToday(appointment.appointment_date);

    if (availableActions.length === 0) {
      return (
        <div className="flex flex-col space-y-2">
          <span className="text-xs text-gray-500 px-3 py-1.5 bg-gray-100/50 backdrop-blur-sm rounded-lg text-center">
            {isPastDate ? 'Cita pasada' : 'Sin acciones'}
          </span>
        </div>
      );
    }

    return (
      <div className="flex flex-col space-y-2">
        {isTodayDate && (
          <span className="text-xs text-blue-600 font-medium px-3 py-1.5 bg-blue-100/50 backdrop-blur-sm rounded-lg text-center flex items-center justify-center gap-1">
            <Calendar className="w-3 h-3" />
            HOY
          </span>
        )}
        
        {availableActions.includes('confirmar') && (
          <button
            onClick={() => handleAppointmentAction(appointment.id, 'confirmar')}
            disabled={actionLoading}
            className="bg-green-100/50 backdrop-blur-sm text-green-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-green-200/50 disabled:opacity-50 transition-all flex items-center justify-center gap-1"
          >
            <CheckCircle className="w-3.5 h-3.5" />
            Confirmar
          </button>
        )}
        
        {availableActions.includes('completar') && (
          <button
            onClick={() => handleAppointmentAction(appointment.id, 'completar')}
            disabled={actionLoading}
            className="bg-purple-100/50 md:backdrop-blur-sm text-purple-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-purple-200/50 disabled:opacity-50 transition-all flex items-center justify-center gap-1"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Completar
          </button>
        )}
        
        {availableActions.includes('cancelar') && (
          <button
            onClick={() => handleAppointmentAction(appointment.id, 'cancelar')}
            disabled={actionLoading}
            className="bg-red-100/50 backdrop-blur-sm text-red-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-red-200/50 disabled:opacity-50 transition-all flex items-center justify-center gap-1"
          >
            <XCircle className="w-3.5 h-3.5" />
            Cancelar
          </button>
        )}

        {availableActions.includes('reagendar') && (
          <button
            onClick={() => handleReschedule(appointment)}
            disabled={actionLoading}
            className="bg-blue-100/50 backdrop-blur-sm text-blue-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-200/50 disabled:opacity-50 transition-all flex items-center justify-center gap-1"
          >
            <Calendar className="w-3.5 h-3.5" />
            Reagendar
          </button>
        )}
      </div>
    );
  };

  const renderAppointmentCard = (appointment: any) => {
    const statusColors = {
      pending: 'bg-yellow-100/50 text-yellow-700',
      confirmed: 'bg-green-100/50 text-green-700',
      completed: 'bg-purple-100/50 text-purple-700',
      cancelled: 'bg-red-100/50 text-red-700',
      no_show: 'bg-gray-100/50 text-gray-700'
    };

    const statusLabels = {
      pending: 'Pendiente',
      confirmed: 'Confirmada',
      completed: 'Completada',
      cancelled: 'Cancelada',
      no_show: 'No asistió'
    };

    return (
      <div key={appointment.id} className="bg-white md:bg-white/80 md:backdrop-blur-sm border border-white/20 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pink-500 md:bg-gradient-to-br md:from-pink-400 md:to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                {appointment.client_display_name?.charAt(0) || 'C'}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{appointment.client_display_name || 'Cliente'}</h3>
                <p className="text-sm text-gray-600">{appointment.service_name}</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {appointment.appointment_time}
              </span>
              <span className={`px-2 py-1 rounded-lg text-xs font-medium backdrop-blur-sm ${statusColors[appointment.status as keyof typeof statusColors]}`}>
                {statusLabels[appointment.status as keyof typeof statusLabels]}
              </span>
              {appointment.location_name && (
                <span className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-purple-50 text-purple-700">
                  <MapPin className="w-3 h-3" />
                  {appointment.location_name}
                </span>
              )}
            </div>

            {/* Nota del turno: editable desde el dashboard principal */}
            <AppointmentNoteEditor
              appointmentId={appointment.id}
              value={appointment.notes}
              onSaved={() => refreshData()}
            />

            {appointment.patient_extra_data && Object.keys(appointment.patient_extra_data).length > 0 && (
              <div className="text-xs text-gray-500 bg-amber-50/50 rounded-lg p-2 flex flex-wrap gap-x-3 gap-y-1">
                {Object.entries(appointment.patient_extra_data).filter(([key]) => !['additional_service_ids', 'all_service_names'].includes(key)).map(([key, value]: [string, any]) => (
                  value && (
                    <span key={key}>
                      <span className="font-bold">{getExtraLabel(key)}:</span> {formatExtraValue(key, value)}
                    </span>
                  )
                ))}
              </div>
            )}
          </div>

          {renderAppointmentActions(appointment)}
        </div>
      </div>
    );
  };

  // =====================================================
  // FUNCIONES DE NAVEGACIÓN
  // =====================================================

  const handleNewAppointment = () => navigate('/professional/new-appointment');
  const handleServicesConfig = () => navigate('/professional/services-config');
  const handleScheduleConfig = () => navigate('/professional/schedule-config');
  const handleClientManagement = () => navigate('/professional/clients');
  const handleCalendar = () => navigate('/professional/calendar');
  const handleReports = () => navigate('/professional/reports');
  const handleLocationsConfig = () => navigate('/professional/locations-config');

  const handlePrevDay = () => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() - 1);
    setSelectedDate(currentDate.toISOString().split('T')[0]);
  };

  const handleNextDay = () => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() + 1);
    setSelectedDate(currentDate.toISOString().split('T')[0]);
  };

  const handleToday = () => {
    setSelectedDate(getToday());
  };

  // --- Exportar agenda a Excel (via Backend) ---
  const exportAgendaToExcel = async (fromDate: string, toDate: string) => {
    if (!user?.id) return;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const token = localStorage.getItem('token');
    const response = await fetch(
      `${API_URL}/api/appointments/professional/${user.id}/export/excel?from=${fromDate}&to=${toDate}`,
      { headers: { 'Authorization': `Bearer ${token}` } }
    );
    if (!response.ok) throw new Error('Error al exportar agenda');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agenda_${fromDate}_${toDate}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center">
        <LoadingSpinner size={48} />
      </div>
    );
  }

  // Si la suscripción expiró, mostrar SOLO el modal de pago (nada más)
  if (showExpiredModal) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
        <SubscriptionExpiredModal
          show={true}
          subscriptionStatus={localStorage.getItem('subscription_status') || 'trial_expired'}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        
        {/* Banner de suscripción */}
        <SubscriptionBanner onSubscribeClick={() => setShowSubscriptionModal(true)} />

        {/* Cuándo vence, a la vista. El dato existía solo en "Mi suscripción",
            y a esa pantalla se llega nada más que desde el menú del Header:
            hay que ir a buscar algo que no sabés que existe. */}
        <div className="mb-4">
          <VencimientoSuscripcion />
        </div>

        {/* Banner finito: "vence mañana / vence hoy" — auto-dismiss a los 5s */}
        <SubscriptionExpirySoonBanner
          subscription={user && (user as any).subscription}
          onPayClick={() => setShowSubscriptionModal(true)}
        />

        {/* Header del perfil */}
        <div className="animate-slide-in-left" style={{ animationDelay: '0ms' }}>
          <DashboardProfileHeader professional={professional} />
        </div>
      {/* 🆕 Link de reservas + Acceso rápido a recordatorios */}
        {user && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 animate-slide-in-right" style={{ animationDelay: '70ms' }}>
            {professional?.slug && (
              <ShareProfileButton
                professionalId={user.id}
                slug={professional.slug}
                className="mb-0"
                professionalData={{
                  nombre: professional.nombre,
                  apellido: professional.apellido,
                  especialidad: professional.especialidad,
                  professional_type: professional.professional_type,
                  business_name: professional.business_name,
                  picture: professional.picture,
                }}
              />
            )}
            <RemindersQuickAccess
              professionalId={user.id}
              onOpen={() => setShowRemindersPanel(true)}
              onOpenGuide={() => setShowRemindersGuide(true)}
            />
          </div>
        )}
        {/* Métricas principales */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-slide-in-left" style={{ animationDelay: '140ms' }}>
          {loadingMetrics ? (
            <>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white md:bg-white/80 md:backdrop-blur-sm border border-white/20 rounded-xl p-4 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-12"></div>
                </div>
              ))}
            </>
          ) : metricsError ? (
            <div className="col-span-2 md:col-span-4 bg-red-100/50 backdrop-blur-sm border border-red-200 rounded-xl p-4 text-red-600 text-center">
              Error cargando métricas
            </div>
          ) : (
            <>
              <div 
                onClick={() => document.getElementById('calendar-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-white md:bg-white/80 md:backdrop-blur-sm border border-white/20 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
              >
                <p className="text-sm text-gray-600 mb-1">Citas Hoy</p>
                <p className="text-3xl font-bold text-pink-600">{metrics?.todayAppointments || 0}</p>
              </div>
              <div 
                onClick={() => document.getElementById('calendar-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-white md:bg-white/80 md:backdrop-blur-sm border border-white/20 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
              >
                <p className="text-sm text-gray-600 mb-1">Esta Semana</p>
                <p className="text-3xl font-bold text-purple-600">{metrics?.weeklyAppointments || 0}</p>
              </div>
              <div 
                onClick={handleClientManagement}
                className="bg-white md:bg-white/80 md:backdrop-blur-sm border border-white/20 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
              >
                <p className="text-sm text-gray-600 mb-1">Clientes Activas</p>
                <p className="text-3xl font-bold text-blue-600">{metrics?.totalClients || 0}</p>
              </div>
              <div 
                onClick={() => document.getElementById('calendar-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-white md:bg-white/80 md:backdrop-blur-sm border border-white/20 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
              >
                <p className="text-sm text-gray-600 mb-1">Pendientes</p>
                <p className="text-3xl font-bold text-orange-600">{metrics?.pendingAppointments || 0}</p>
              </div>
            </>
          )}
        </div>

        {/* Indicador de cuota WhatsApp */}
        <WhatsAppQuotaIndicator />

        {/* Control de fecha */}
        <div id="calendar-section" className="bg-white md:bg-white/80 md:backdrop-blur-sm border border-white/20 rounded-xl p-4 shadow-lg animate-slide-in-right" style={{ animationDelay: '210ms' }}>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <button
              onClick={handlePrevDay}
              className="w-full sm:w-auto bg-white/50 hover:bg-white/70 backdrop-blur-sm text-gray-700 px-4 py-2 rounded-lg transition-all flex items-center justify-center gap-2"
            >
              <ChevronLeft className="w-5 h-5" />
              Anterior
            </button>
            
            <div className="flex items-center gap-2">
              <button
                onClick={handleToday}
                className="bg-pink-500 md:bg-gradient-to-r md:from-pink-500 md:to-purple-500 text-white px-6 py-2 rounded-lg font-medium hover:shadow-lg transition-all"
              >
                Hoy
              </button>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-white md:bg-white/80 md:backdrop-blur-sm border border-white/40 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>
            
            <button
              onClick={handleNextDay}
              className="w-full sm:w-auto bg-white/50 hover:bg-white/70 backdrop-blur-sm text-gray-700 px-4 py-2 rounded-lg transition-all flex items-center justify-center gap-2"
            >
              Siguiente
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Botón de nueva cita */}
        <button
          onClick={handleNewAppointment}
          className="w-full bg-pink-500 md:bg-gradient-to-r md:from-pink-500 md:to-purple-500 text-white py-4 rounded-xl font-semibold text-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
        >
          <Plus className="w-6 h-6" />
          Nueva Cita
        </button>

        {/* Grid de acciones rápidas */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 animate-slide-in-left" style={{ animationDelay: '280ms' }}>
          <button 
            onClick={handleServicesConfig}
            className="bg-white md:bg-white/80 md:backdrop-blur-sm border border-white/20 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all group"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-500 rounded-xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                <Settings className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Servicios</span>
            </div>
          </button>
          
          <button
            onClick={handleScheduleConfig}
            className="bg-white md:bg-white/80 md:backdrop-blur-sm border border-white/20 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all group"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Horarios</span>
            </div>
          </button>

          <button
            onClick={handleLocationsConfig}
            className="bg-white md:bg-white/80 md:backdrop-blur-sm border border-white/20 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all group"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-violet-500 rounded-xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Sedes</span>
            </div>
          </button>

          <button
            onClick={() => navigate('/professional/portfolio')}
            className="bg-white md:bg-white/80 md:backdrop-blur-sm border border-white/20 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all group"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-pink-500 md:bg-gradient-to-br md:from-pink-400 md:to-purple-500 rounded-xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                <Image className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Portfolio Público</span>
            </div>
          </button>
          
          <button 
            onClick={handleClientManagement}
            className="bg-white md:bg-white/80 md:backdrop-blur-sm border border-white/20 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all group"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                <Users className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Clientas</span>
            </div>
          </button>
          
          <button 
            onClick={handleCalendar}
            className="bg-white md:bg-white/80 md:backdrop-blur-sm border border-white/20 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all group"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Calendario</span>
            </div>
          </button>
          
          <button 
            onClick={() => navigate('/professional/payment-config')}
            className="bg-white md:bg-white/80 md:backdrop-blur-sm border border-white/20 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all group"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-teal-500 rounded-xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Pagos</span>
            </div>
          </button>
          
          <button 
            onClick={() => navigate('/professional/notification-config')}
            className="bg-white md:bg-white/80 md:backdrop-blur-sm border border-white/20 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all group"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">WhatsApp</span>
            </div>
          </button>
          
          <button 
            onClick={() => navigate('/professional/payment-verification')}
            className="bg-white md:bg-white/80 md:backdrop-blur-sm border border-white/20 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all group"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                <CreditCard className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Verificar Transferencias</span>
            </div>
          </button>
          
          <button
            onClick={handleReports}
            className="bg-white md:bg-white/80 md:backdrop-blur-sm border border-white/20 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all group"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Reportes</span>
            </div>
          </button>

          <button
            onClick={() => navigate('/professional/booking-fields-config')}
            className="bg-white md:bg-white/80 md:backdrop-blur-sm border border-white/20 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all group"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                <ListChecks className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Datos del Turno</span>
            </div>
          </button>
        </div>

        {/* Lista de citas - Full Width */}
        <div className="bg-white md:bg-white/80 md:backdrop-blur-sm border border-white/20 rounded-xl shadow-lg">
          <div className="p-6 border-b border-white/20">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Citas para {formatLocalDate(selectedDate)}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowRemindersPanel(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm font-medium shadow-sm"
                  title="Ver turnos para enviar recordatorios por WhatsApp"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span className="hidden sm:inline">Recordatorios</span>
                </button>
                <button
                  onClick={() => setShowRemindersGuide(true)}
                  className="flex items-center justify-center w-8 h-8 text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                  title="¿Cómo funcionan los recordatorios?"
                  aria-label="Ver guía de recordatorios"
                >
                  <HelpCircle className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setShowExportModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 rounded-lg transition-colors text-sm font-medium"
                  title="Exportar agenda a Excel"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  <span className="hidden sm:inline">Excel</span>
                </button>
                {loadingAppointments && (
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pink-500"></div>
                )}
              </div>
            </div>
          </div>
          <div className="p-6">
            {appointmentsError ? (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <p className="text-red-600 mb-4">Error cargando citas: {appointmentsError}</p>
                <button 
                  onClick={() => user?.id && refreshData()}
                  className="bg-red-100/50 backdrop-blur-sm text-red-600 px-4 py-2 rounded-lg hover:bg-red-200/50 transition-all"
                >
                  Reintentar
                </button>
              </div>
            ) : loadingAppointments ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse">
                    <div className="h-20 bg-gray-200/50 backdrop-blur-sm rounded-lg"></div>
                  </div>
                ))}
              </div>
            ) : appointments.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-pink-500 md:bg-gradient-to-br md:from-pink-400 md:to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No hay citas programadas</h3>
                <p className="text-gray-600 mb-6">
                  No tienes citas para {formatLocalDate(selectedDate)}
                </p>
                <button
                  onClick={handleNewAppointment}
                  className="bg-pink-500 md:bg-gradient-to-r md:from-pink-500 md:to-purple-500 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all inline-flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Agregar Primera Cita
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {appointments.map(renderAppointmentCard)}
              </div>
            )}
          </div>
        </div>

        {/* Error de acciones */}
        {actionError && (
          <div className="fixed bottom-4 right-4 bg-red-100/90 backdrop-blur-md border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-lg z-50">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <span>{actionError}</span>
              <button 
                onClick={clearActionState}
                className="ml-4 text-red-500 hover:text-red-700"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Modal de reagendamiento */}
        {showRescheduleModal && selectedAppointmentForReschedule && (
          <RescheduleModal
            appointment={selectedAppointmentForReschedule}
            isOpen={showRescheduleModal}
            onClose={() => {
              setShowRescheduleModal(false);
              setSelectedAppointmentForReschedule(null);
            }}
            onSuccess={handleRescheduleSuccess}
          />
        )}

        {/* Modal de suscripción */}
        <SubscriptionModal
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
          daysRemaining={daysRemaining}
          isExpired={isExpired}
        />

        {/* Modal de suscripción vencida */}
        <SubscriptionExpiredModal 
          show={showExpiredModal} 
          subscriptionStatus={localStorage.getItem('subscription_status') || 'trial_expired'} 
        />

        {/* 🆕 CHECKER DE ONBOARDING - VERIFICACIÓN INDEPENDIENTE */}
        {user && !showExpiredModal && (
          <OnboardingChecker
            userId={user.id}
            slug={professional?.slug}
            userName={user.nombre}
            userType="belleza"
            especialidad={user.especialidad}
            onComplete={() => {
              console.log('✅ Onboarding completado - Recargando datos...');
              refreshData();
            }}
            onSkip={() => {
              console.log('⏭️ Onboarding saltado');
            }}
          />
        )}

        {/* Panel de debug solo en desarrollo */}
        {process.env.NODE_ENV === 'development' && (
          <div className="bg-blue-50/90 backdrop-blur-sm border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-800 mb-2">🔧 Estado de Conexión (Solo desarrollo)</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-blue-700">
              <div>
                <span className="font-medium">Métricas:</span>
                <span className={loadingMetrics ? 'text-yellow-600' : metricsError ? 'text-red-600' : 'text-green-600'}>
                  {loadingMetrics ? ' Cargando...' : metricsError ? ' Error' : ' ✓ Conectado'}
                </span>
              </div>
              <div>
                <span className="font-medium">Citas:</span>
                <span className={loadingAppointments ? 'text-yellow-600' : appointmentsError ? 'text-red-600' : 'text-green-600'}>
                  {loadingAppointments ? ' Cargando...' : appointmentsError ? ' Error' : ` ✓ ${appointments.length} citas`}
                </span>
              </div>
              <div>
                <span className="font-medium">Servicios:</span>
                <span className={loadingServices ? 'text-yellow-600' : servicesError ? 'text-red-600' : 'text-green-600'}>
                  {loadingServices ? ' Cargando...' : servicesError ? ' Error' : ` ✓ ${services.length} servicios`}
                </span>
              </div>
              <div>
                <span className="font-medium">Usuario:</span>
                <span className="text-green-600">✓ ID: {user?.id}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Exportar Agenda */}
      <ExportDateRangeModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={exportAgendaToExcel}
        title="Exportar Agenda"
        description="Selecciona el rango de fechas para exportar"
      />

      {/* Panel de Recordatorios WhatsApp (manual) */}
      {user?.id && (
        <RemindersPanel
          professionalId={user.id}
          open={showRemindersPanel}
          onClose={() => setShowRemindersPanel(false)}
        />
      )}

      {/* Guía wizard de recordatorios manuales (primer uso) */}
      <NewRemindersGuideModal
        open={showRemindersGuide}
        onClose={() => setShowRemindersGuide(false)}
      />
    </div>
  );
};

export default BeautyProfessionalDashboard;